// @givmo/app-charity — the director family's guards (CBG-SEC2 SEC2-WEB; contract §2.1).
//
// A director is a Givmo account (D-CBG-16), signed in through this console's own chain — so a
// director's account gets its `users` row at the one chokepoint every sign-in passes
// (`ensureBackendUser`, the suite session). But the family is NOT the officer's authenticated
// layout: no memberships read, no portal-entry gate, no console shell. These four guards are the
// whole of its routing rule:
//
//   * a board page needs an account, and a confirmed address where the account's sign-in can be
//     repaired by the emailed code (`requiresEmailVerification`: password or Microsoft);
//   * the door and the email forms send a signed-in visitor back where they were going;
//   * the invitation opens signed out (opening it spends nothing), but a signed-in, unconfirmed
//     account confirms its address before it can join;
//   * the confirmation needs an account, and sends a confirmed one on.
//
// Every `redirect` is sanitized with the console's own rule, and any target is still guarded by its
// own route. Each decides on the settled session (CPA-WEB86, `decideOnSettledSession`): a director
// door navigates the moment its sign-in resolves, before the session is published, and a guard
// reading that snapshot sent the director straight back to the door.

import { redirect } from '@tanstack/react-router';
import { decideOnSettledSession, requiresEmailVerification } from '../../auth/auth-context';
import type { CharityRouterContext } from '../../router/context';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { DIRECTOR_ROUTES } from './director-routes';

interface GuardArgs {
  context: CharityRouterContext;
  location: { href: string };
}

/** The board pages: signed out → the door; an unconfirmed, repairable account → the confirmation. */
export function requireDirectorAccount({ context, location }: GuardArgs) {
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) {
      throw redirect({ to: DIRECTOR_ROUTES.signIn, search: { redirect: location.href } });
    }
    if (requiresEmailVerification(session.user)) {
      throw redirect({ to: DIRECTOR_ROUTES.confirmEmail, search: { redirect: location.href } });
    }
  });
}

/** The door and the email forms: a signed-in visitor goes back where they were going. */
export function redirectSignedInDirector({
  context,
  search,
}: GuardArgs & { search: { redirect?: string } }) {
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) return;
    const target = sanitizeRedirectTarget(search.redirect);
    if (requiresEmailVerification(session.user)) {
      throw redirect({
        to: DIRECTOR_ROUTES.confirmEmail,
        search: target === undefined ? {} : { redirect: target },
      });
    }
    throw redirect({ to: target ?? DIRECTOR_ROUTES.home, replace: true });
  });
}

/** The invitation: open signed out; a signed-in, unconfirmed account confirms its address first. */
export function confirmDirectorEmailFirst({ context, location }: GuardArgs) {
  return decideOnSettledSession(context.auth, (session) => {
    if (session.isAuthenticated && requiresEmailVerification(session.user)) {
      throw redirect({ to: DIRECTOR_ROUTES.confirmEmail, search: { redirect: location.href } });
    }
  });
}

/** The confirmation: signed out → the door, keeping the target; confirmed → on to it. */
export function requireDirectorConfirmation({
  context,
  search,
}: GuardArgs & { search: { redirect?: string } }) {
  const target = sanitizeRedirectTarget(search.redirect);
  return decideOnSettledSession(context.auth, (session) => {
    if (!session.isAuthenticated) {
      throw redirect({
        to: DIRECTOR_ROUTES.signIn,
        search: target === undefined ? {} : { redirect: target },
      });
    }
    if (!requiresEmailVerification(session.user)) {
      throw redirect({ to: target ?? DIRECTOR_ROUTES.home, replace: true });
    }
  });
}
